import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import MediaImageField from "@/components/common/MediaImageField";
import DragList from "@/components/common/DragList";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";

const PANELS = [
  { key: "brand", label: "Brand", icon: "Palette", blurb: "Logo & identity" },
  { key: "hero", label: "Hero", icon: "Sparkles", blurb: "First impression" },
  { key: "about", label: "About", icon: "Info", blurb: "Story copy" },
  { key: "services-header", label: "Services", icon: "Wrench", blurb: "Services heading" },
  { key: "section-headers", label: "Sections", icon: "LayoutTemplate", blurb: "Section titles" },
  { key: "cta", label: "CTA", icon: "Zap", blurb: "Contact form block" },
  { key: "careers", label: "Careers", icon: "Briefcase", blurb: "Jobs & perks" },
  { key: "contact", label: "Contact", icon: "Mail", blurb: "Contact details" },
  { key: "footer", label: "Footer", icon: "PanelBottom", blurb: "Links & socials" },
];

const SECTION_HEADER_KEYS = [
  { key: "why_choose", label: "Why Choose Us" },
  { key: "process", label: "Process Steps" },
  { key: "challenges", label: "Challenges" },
  { key: "industries", label: "Industries" },
  { key: "stats", label: "Stats" },
  { key: "logos", label: "Client Logos" },
  { key: "testimonials", label: "Testimonials" },
  { key: "team", label: "Team" },
  { key: "faq", label: "FAQ" },
  { key: "careers", label: "Careers" },
  { key: "portfolio", label: "Portfolio" },
  { key: "pricing", label: "Pricing" },
  { key: "blog", label: "Blog" },
  { key: "contact_page", label: "Contact Page" },
];

function useSiteSection(endpoint) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/site/${endpoint}`);
      setData(d || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line
  const { dirty, markClean } = useUnsavedGuard(data, { ready: !loading });
  return { data, setData, loading, dirty, markClean };
}

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-[10px] uppercase tracking-[0.14em] text-white/45">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-white/35 leading-relaxed">{hint}</p>}
  </div>
);

const inputClass = "bg-black/25 border-white/10 text-white placeholder:text-white/25 focus-visible:ring-cyan-400/30";

function StudioShell({
  title, description, dirty, saving, onSave, preview, children, loading,
}) {
  if (loading) {
    return <div className="text-sm text-white/40 py-16 text-center">Loading…</div>;
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white tracking-tight">{title}</h2>
          <p className="text-xs text-white/45 mt-0.5 max-w-xl">{description}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${dirty ? "text-amber-300" : "text-emerald-300/80"}`}>
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <Button onClick={onSave} disabled={saving || !dirty} className="btn-gradient text-white border-0 h-9">
            <Icons.Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
        <GlassCard className="p-5 min-h-[360px] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{
            background: "radial-gradient(ellipse at top, rgba(34,211,238,0.08), transparent 55%)",
          }} />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/35 mb-4">Live preview</div>
            {preview}
          </div>
        </GlassCard>
        <GlassCard className="p-4 space-y-4 xl:sticky xl:top-20 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/35">Inspector</div>
          {children}
        </GlassCard>
      </div>
    </div>
  );
}

/* ── Brand ── */
function BrandPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("brand");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/brand", data);
      markClean(data);
      toast.success("Brand saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="Brand identity"
      description="Logo, wordmark, and mark shown in the site navigation and browser tab."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-transparent p-8">
          <div className="flex items-center gap-4">
            {data.logo_url ? (
              <img src={data.logo_url} alt="" className="w-14 h-14 object-contain rounded-xl bg-white p-1.5 shadow-lg" />
            ) : (
              <div className="w-14 h-14 rounded-xl btn-gradient flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {data.mark || data.name?.slice(0, 2) || "CM"}
              </div>
            )}
            <div>
              {(data.showText !== false || data.is_wordmark) && (
                <div className="text-xl font-semibold text-white tracking-tight">
                  {data.name || "Brand name"}{" "}
                  <span className="text-cyan-200/90">{data.suffix || ""}</span>
                </div>
              )}
              <p className="text-xs text-white/40 mt-1">
                {data.is_wordmark ? "Wordmark mode" : "Logo + text"} · Favicon {data.favicon_url ? "set" : "missing"}
              </p>
            </div>
          </div>
        </div>
      )}
    >
      <Field label="Brand name"><Input value={data.name || ""} onChange={(e) => setData({ ...data, name: e.target.value })} className={inputClass} /></Field>
      <Field label="Suffix"><Input value={data.suffix || ""} onChange={(e) => setData({ ...data, suffix: e.target.value })} placeholder="Solutions" className={inputClass} /></Field>
      <Field label="Logo mark" hint="Fallback letters when no logo image"><Input value={data.mark || ""} onChange={(e) => setData({ ...data, mark: e.target.value })} placeholder="CM" className={inputClass} /></Field>
      <MediaImageField label="Logo" value={data.logo_url || ""} onChange={(url) => setData({ ...data, logo_url: url })} folder="brand" aspectClass="h-24" />
      <MediaImageField label="Favicon" value={data.favicon_url || ""} onChange={(url) => setData({ ...data, favicon_url: url })} folder="brand" aspectClass="h-20" />
      <div className="flex items-center justify-between gap-3 py-1">
        <span className="text-xs text-white/60">Wordmark (text logo)</span>
        <Switch checked={!!data.is_wordmark} onCheckedChange={(v) => setData({ ...data, is_wordmark: v })} />
      </div>
      <div className="flex items-center justify-between gap-3 py-1">
        <span className="text-xs text-white/60">Show brand text with logo</span>
        <Switch checked={data.showText !== false} onCheckedChange={(v) => setData({ ...data, showText: v })} />
      </div>
    </StudioShell>
  );
}

/* ── Hero ── */
function HeroPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("hero");
  const [saving, setSaving] = useState(false);

  const tags = (data.subheading_items && data.subheading_items.length)
    ? [...data.subheading_items]
    : (data.subheading || "").split("\u2022").map((s) => s.trim()).filter(Boolean);

  const setTags = (items) => {
    setData({
      ...data,
      subheading_items: items,
      subheading: items.filter(Boolean).join(" \u2022 "),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/hero", data);
      markClean(data);
      toast.success("Hero saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="Hero section"
      description="Background, headline, service tags, CTAs, and portfolio dropdown. Drag tags and links to reorder."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="rounded-2xl overflow-hidden border border-white/10 relative min-h-[280px]">
          {data.media?.src ? (
            <img src={data.media.src} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-950" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-black/25" />
          <div className="relative p-8 flex flex-col justify-end min-h-[280px]">
            <h3 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight max-w-lg">
              {data.title_lead || "Headline"}{" "}
              <span className="text-cyan-200">{data.title_accent || ""}</span>
            </h3>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {tags.map((t, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full text-[11px] bg-white/15 text-white/90 border border-white/20 backdrop-blur-sm">{t}</span>
                ))}
              </div>
            )}
            {data.subtitle && <p className="text-sm text-white/70 mt-3 max-w-md line-clamp-2">{data.subtitle}</p>}
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="inline-flex px-3 py-1.5 rounded-md btn-gradient text-xs text-white">{data.primary_cta?.label || "Primary CTA"}</span>
              <span className="inline-flex px-3 py-1.5 rounded-md border border-white/25 text-xs text-white/85">{data.secondary_cta?.label || "Secondary"}</span>
            </div>
          </div>
        </div>
      )}
    >
      <MediaImageField label="Background" value={data.media || null} onChange={(media) => setData({ ...data, media })} folder="hero" valueMode="media" aspectClass="h-32" />
      <Field label="Title lead"><Input value={data.title_lead || ""} onChange={(e) => setData({ ...data, title_lead: e.target.value })} className={inputClass} /></Field>
      <Field label="Title accent"><Input value={data.title_accent || ""} onChange={(e) => setData({ ...data, title_accent: e.target.value })} className={inputClass} /></Field>
      <Field label="Service tags" hint="Drag to reorder · shown as pills under the headline">
        <DragList
          items={tags}
          onChange={setTags}
          emptyText="No tags yet"
          addLabel="Add tag"
          onAdd={() => setTags([...tags, "New tag"])}
          renderItem={(item, _i, { updateValue }) => (
            <Input value={item} onChange={(e) => updateValue(e.target.value)} className={`${inputClass} text-xs h-8`} />
          )}
        />
      </Field>
      <Field label="Subtitle"><Textarea value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} rows={2} className={inputClass} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Primary CTA"><Input value={data.primary_cta?.label || ""} onChange={(e) => setData({ ...data, primary_cta: { ...data.primary_cta, label: e.target.value } })} className={inputClass} /></Field>
        <Field label="Primary link"><Input value={data.primary_cta?.href || ""} onChange={(e) => setData({ ...data, primary_cta: { ...data.primary_cta, href: e.target.value } })} className={inputClass} /></Field>
        <Field label="Secondary CTA"><Input value={data.secondary_cta?.label || ""} onChange={(e) => setData({ ...data, secondary_cta: { ...data.secondary_cta, label: e.target.value } })} className={inputClass} /></Field>
        <Field label="Secondary link"><Input value={data.secondary_cta?.href || ""} onChange={(e) => setData({ ...data, secondary_cta: { ...data.secondary_cta, href: e.target.value } })} className={inputClass} /></Field>
      </div>
      <Field label="Portfolio dropdown" hint="Drag to reorder links in the View Portfolio menu">
        <DragList
          items={data.portfolio_links || []}
          onChange={(portfolio_links) => setData({ ...data, portfolio_links })}
          emptyText="No portfolio links"
          addLabel="Add link"
          onAdd={() => setData({ ...data, portfolio_links: [...(data.portfolio_links || []), { label: "", href: "" }] })}
          renderItem={(link, _i, { update }) => (
            <div className="space-y-1.5">
              <Input value={link.label || ""} onChange={(e) => update({ label: e.target.value })} placeholder="Label" className={`${inputClass} text-xs h-8`} />
              <Input value={link.href || ""} onChange={(e) => update({ href: e.target.value })} placeholder="https://… or /media/…" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>
    </StudioShell>
  );
}

/* ── About ── */
function AboutPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("about");
  const [saving, setSaving] = useState(false);
  const paragraphs = Array.isArray(data.body) ? data.body : [];
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/about", data);
      markClean(data);
      toast.success("About saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="About section"
      description="Label, heading, and body paragraphs. Drag paragraphs to change reading order."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="space-y-3 max-w-xl">
          <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80">{data.label || "About"}</span>
          <h3 className="text-2xl font-semibold text-white tracking-tight">{data.title || "About title"}</h3>
          <div className="space-y-2">
            {paragraphs.map((p, i) => <p key={i} className="text-sm text-white/65 leading-relaxed">{p}</p>)}
            {paragraphs.length === 0 && <p className="text-sm text-white/35">Add body paragraphs in the inspector.</p>}
          </div>
          {data.focus && <p className="text-sm text-cyan-100/90 font-medium pt-2 border-t border-white/10">{data.focus}</p>}
        </div>
      )}
    >
      <Field label="Section label"><Input value={data.label || ""} onChange={(e) => setData({ ...data, label: e.target.value })} className={inputClass} /></Field>
      <Field label="Title"><Input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputClass} /></Field>
      <Field label="Body paragraphs" hint="Drag to reorder">
        <DragList
          items={paragraphs}
          onChange={(body) => setData({ ...data, body })}
          emptyText="No paragraphs"
          addLabel="Add paragraph"
          onAdd={() => setData({ ...data, body: [...paragraphs, ""] })}
          renderItem={(item, _i, { updateValue }) => (
            <Textarea value={item} onChange={(e) => updateValue(e.target.value)} rows={2} className={`${inputClass} text-xs`} />
          )}
        />
      </Field>
      <Field label="Focus line"><Input value={data.focus || ""} onChange={(e) => setData({ ...data, focus: e.target.value })} className={inputClass} /></Field>
    </StudioShell>
  );
}

/* ── Services header ── */
function ServicesHeaderPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("services-header");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/services-header", data);
      markClean(data);
      toast.success("Services header saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };
  return (
    <StudioShell
      title="Services header"
      description="Heading shown above the services grid on the homepage."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="text-center space-y-2 py-10">
          <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80">{data.label || "Services"}</span>
          <h3 className="text-2xl font-semibold text-white">{data.title || "Services title"}</h3>
          <p className="text-sm text-white/55 max-w-md mx-auto">{data.subtitle || "Supporting copy"}</p>
        </div>
      )}
    >
      <Field label="Label"><Input value={data.label || ""} onChange={(e) => setData({ ...data, label: e.target.value })} className={inputClass} /></Field>
      <Field label="Title"><Input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputClass} /></Field>
      <Field label="Subtitle"><Textarea value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} rows={3} className={inputClass} /></Field>
    </StudioShell>
  );
}

/* ── Section headers ── */
function SectionHeadersPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("sections");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState("why_choose");
  const headers = data.headers || {};
  const current = headers[selected] || {};

  const setHeader = (field, value) => {
    setData({
      ...data,
      headers: { ...headers, [selected]: { ...current, [field]: value } },
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/sections", data);
      markClean(data);
      toast.success("Section headers saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const meta = SECTION_HEADER_KEYS.find((s) => s.key === selected);

  return (
    <StudioShell
      title="Section headings"
      description="Pick a homepage section and edit its label, title, and subtitle. Empty fields use site defaults."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1.5">
            {SECTION_HEADER_KEYS.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSelected(s.key)}
                className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                  selected === s.key
                    ? "bg-cyan-400/15 border-cyan-400/40 text-cyan-100"
                    : "border-white/10 text-white/50 hover:border-white/25 hover:text-white/80"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-white/10 p-8 text-center bg-white/[0.03]">
            <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80">{current.label || meta?.label}</span>
            <h3 className="text-2xl font-semibold text-white mt-2">{current.title || `${meta?.label} heading`}</h3>
            <p className="text-sm text-white/55 mt-2 max-w-md mx-auto">{current.subtitle || "Supporting description for this section."}</p>
          </div>
        </div>
      )}
    >
      <Field label="Editing">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className={`w-full rounded-md px-3 py-2 text-sm ${inputClass}`}
        >
          {SECTION_HEADER_KEYS.map((s) => (
            <option key={s.key} value={s.key} className="bg-slate-900">{s.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Section label"><Input value={current.label || ""} onChange={(e) => setHeader("label", e.target.value)} className={inputClass} /></Field>
      <Field label="Title"><Input value={current.title || ""} onChange={(e) => setHeader("title", e.target.value)} className={inputClass} /></Field>
      <Field label="Subtitle"><Textarea value={current.subtitle || ""} onChange={(e) => setHeader("subtitle", e.target.value)} rows={3} className={inputClass} /></Field>
    </StudioShell>
  );
}

/* ── CTA ── */
function CtaPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("cta");
  const [saving, setSaving] = useState(false);
  const options = data.service_options || [];
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/cta", data);
      markClean(data);
      toast.success("CTA saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="CTA / contact block"
      description="Form heading, service dropdown options, and optional side image. Drag options to reorder."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="grid sm:grid-cols-2 gap-4 items-stretch">
          <div className="rounded-2xl border border-white/10 p-6 bg-white/[0.04] space-y-3">
            <span className="text-[10px] uppercase tracking-[0.16em] text-cyan-200/80">{data.label || "Get started"}</span>
            <h3 className="text-xl font-semibold text-white">{data.title || "CTA title"}</h3>
            <p className="text-sm text-white/55 line-clamp-3">{data.subtitle || "Supporting copy"}</p>
            <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-white/50">
              {options[0] || "Service option"} ▾
            </div>
            <span className="inline-flex px-3 py-1.5 rounded-md btn-gradient text-xs text-white">{data.submit_label || "Submit"}</span>
          </div>
          <div className="rounded-2xl border border-white/10 overflow-hidden min-h-[160px] bg-black/30">
            {data.media?.src ? (
              <img src={data.media.src} alt="" className="w-full h-full object-cover min-h-[160px]" />
            ) : (
              <div className="h-full min-h-[160px] grid place-items-center text-xs text-white/30">Side image</div>
            )}
          </div>
        </div>
      )}
    >
      <Field label="Badge"><Input value={data.label || ""} onChange={(e) => setData({ ...data, label: e.target.value })} className={inputClass} /></Field>
      <Field label="Title"><Input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputClass} /></Field>
      <Field label="Subtitle"><Textarea value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} rows={3} className={inputClass} /></Field>
      <Field label="Submit button"><Input value={data.submit_label || ""} onChange={(e) => setData({ ...data, submit_label: e.target.value })} className={inputClass} /></Field>
      <MediaImageField label="Side image" value={data.media || null} onChange={(media) => setData({ ...data, media })} folder="cta" valueMode="media" aspectClass="h-28" />
      <Field label="Service options" hint="Drag to reorder dropdown choices">
        <DragList
          items={options}
          onChange={(service_options) => setData({ ...data, service_options })}
          emptyText="No options"
          addLabel="Add option"
          onAdd={() => setData({ ...data, service_options: [...options, "New service"] })}
          renderItem={(item, _i, { updateValue }) => (
            <Input value={item} onChange={(e) => updateValue(e.target.value)} className={`${inputClass} text-xs h-8`} />
          )}
        />
      </Field>
    </StudioShell>
  );
}

/* ── Careers ── */
function CareersPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("careers");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/careers", data);
      markClean(data);
      toast.success("Careers saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="Careers"
      description="Page toggle, openings, and perks. Drag rows to reorder how they appear on the site."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${data.enabled ? "bg-emerald-400" : "bg-white/30"}`} />
            <span className="text-xs text-white/50">{data.enabled ? "Page enabled" : "Page disabled"}</span>
          </div>
          <h3 className="text-2xl font-semibold text-white">{data.headline || "Join the team"}</h3>
          <p className="text-sm text-white/55">{data.subtitle || ""}</p>
          <div className="space-y-2">
            {(data.openings || []).slice(0, 3).map((o, i) => (
              <div key={i} className="rounded-xl border border-white/10 px-4 py-3 flex justify-between gap-3">
                <div>
                  <div className="text-sm text-white font-medium">{o.title || "Role"}</div>
                  <div className="text-[11px] text-white/45">{[o.dept, o.type, o.location].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            ))}
          </div>
          {(data.perks || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {(data.perks || []).slice(0, 6).map((p, i) => (
                <span key={i} className="px-2 py-1 rounded-full text-[11px] border border-white/15 text-white/70">{p}</span>
              ))}
            </div>
          )}
        </div>
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-white/60">Careers page enabled</span>
        <Switch checked={!!data.enabled} onCheckedChange={(v) => setData({ ...data, enabled: v })} />
      </div>
      <Field label="Headline"><Input value={data.headline || ""} onChange={(e) => setData({ ...data, headline: e.target.value })} className={inputClass} /></Field>
      <Field label="Subtitle"><Textarea value={data.subtitle || ""} onChange={(e) => setData({ ...data, subtitle: e.target.value })} rows={2} className={inputClass} /></Field>
      <Field label="CTA label"><Input value={data.cta?.label || ""} onChange={(e) => setData({ ...data, cta: { ...data.cta, label: e.target.value } })} className={inputClass} /></Field>
      <Field label="Job openings" hint="Drag to reorder">
        <DragList
          items={data.openings || []}
          onChange={(openings) => setData({ ...data, openings })}
          emptyText="No openings"
          addLabel="Add opening"
          onAdd={() => setData({ ...data, openings: [...(data.openings || []), { title: "", dept: "", type: "", location: "" }] })}
          renderItem={(o, _i, { update }) => (
            <div className="grid grid-cols-2 gap-1.5">
              <Input value={o.title || ""} onChange={(e) => update({ title: e.target.value })} placeholder="Title" className={`${inputClass} text-xs h-8`} />
              <Input value={o.dept || ""} onChange={(e) => update({ dept: e.target.value })} placeholder="Dept" className={`${inputClass} text-xs h-8`} />
              <Input value={o.type || ""} onChange={(e) => update({ type: e.target.value })} placeholder="Type" className={`${inputClass} text-xs h-8`} />
              <Input value={o.location || ""} onChange={(e) => update({ location: e.target.value })} placeholder="Location" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>
      <Field label="Perks" hint="Drag to reorder">
        <DragList
          items={data.perks || []}
          onChange={(perks) => setData({ ...data, perks })}
          emptyText="No perks"
          addLabel="Add perk"
          onAdd={() => setData({ ...data, perks: [...(data.perks || []), ""] })}
          renderItem={(p, _i, { updateValue }) => (
            <Input value={p} onChange={(e) => updateValue(e.target.value)} className={`${inputClass} text-xs h-8`} />
          )}
        />
      </Field>
    </StudioShell>
  );
}

/* ── Contact ── */
function ContactPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("contact");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/contact", data);
      markClean(data);
      toast.success("Contact saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };
  return (
    <StudioShell
      title="Contact details"
      description="Public location, email, phone, and website shown on contact surfaces."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="rounded-2xl border border-white/10 p-6 space-y-3 bg-white/[0.03]">
          {[
            { icon: Icons.MapPin, v: data.location },
            { icon: Icons.Mail, v: data.email },
            { icon: Icons.Phone, v: data.phone },
            { icon: Icons.Globe, v: data.website },
          ].map(({ icon: I, v }, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-white/70">
              <I className="w-4 h-4 text-cyan-200/70 shrink-0" />
              <span>{v || "—"}</span>
            </div>
          ))}
        </div>
      )}
    >
      <Field label="Location"><Input value={data.location || ""} onChange={(e) => setData({ ...data, location: e.target.value })} className={inputClass} /></Field>
      <Field label="Email"><Input value={data.email || ""} onChange={(e) => setData({ ...data, email: e.target.value })} className={inputClass} /></Field>
      <Field label="Phone"><Input value={data.phone || ""} onChange={(e) => setData({ ...data, phone: e.target.value })} className={inputClass} /></Field>
      <Field label="Website"><Input value={data.website || ""} onChange={(e) => setData({ ...data, website: e.target.value })} className={inputClass} /></Field>
    </StudioShell>
  );
}

/* ── Footer ── */
function FooterPanel() {
  const { data, setData, loading, dirty, markClean } = useSiteSection("footer");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site/footer", data);
      markClean(data);
      toast.success("Footer saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <StudioShell
      title="Footer"
      description="Description, socials, link columns, and contact rows. Drag lists to control display order."
      dirty={dirty} saving={saving} onSave={save} loading={loading}
      preview={(
        <div className="rounded-2xl border border-white/10 p-6 bg-slate-950/60 space-y-4">
          <p className="text-sm text-white/60 line-clamp-2">{data.description || "Footer description"}</p>
          <div className="flex flex-wrap gap-2">
            {(data.socials || []).map((s, i) => (
              <span key={i} className="px-2 py-1 rounded-md text-[11px] border border-white/15 text-white/70">{s.platform || "Social"}</span>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(data.columns || []).map((c, i) => (
              <div key={i}>
                <div className="text-xs font-medium text-white mb-1">{c.title || "Column"}</div>
                <ul className="space-y-0.5">
                  {(c.links || []).slice(0, 3).map((l, j) => (
                    <li key={j} className="text-[11px] text-white/45">{l.label || "Link"}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-white/35 pt-2 border-t border-white/10">{data.copyright || "© Copyright"}</p>
        </div>
      )}
    >
      <Field label="Description"><Textarea value={data.description || ""} onChange={(e) => setData({ ...data, description: e.target.value })} rows={2} className={inputClass} /></Field>
      <Field label="Copyright"><Input value={data.copyright || ""} onChange={(e) => setData({ ...data, copyright: e.target.value })} className={inputClass} /></Field>
      <Field label="Legal links" hint="Pipe-separated"><Input value={data.legal || ""} onChange={(e) => setData({ ...data, legal: e.target.value })} className={inputClass} /></Field>

      <Field label="Social links" hint="Drag to reorder">
        <DragList
          items={data.socials || []}
          onChange={(socials) => setData({ ...data, socials })}
          emptyText="No socials"
          addLabel="Add social"
          onAdd={() => setData({ ...data, socials: [...(data.socials || []), { platform: "", url: "" }] })}
          renderItem={(s, _i, { update }) => (
            <div className="space-y-1.5">
              <Input value={s.platform || ""} onChange={(e) => update({ platform: e.target.value })} placeholder="Platform" className={`${inputClass} text-xs h-8`} />
              <Input value={s.url || ""} onChange={(e) => update({ url: e.target.value })} placeholder="https://…" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>

      <Field label="Footer columns" hint="Drag columns to reorder">
        <DragList
          items={data.columns || []}
          onChange={(columns) => setData({ ...data, columns })}
          emptyText="No columns"
          addLabel="Add column"
          onAdd={() => setData({ ...data, columns: [...(data.columns || []), { title: "", links: [] }] })}
          renderItem={(column, colIdx, { update }) => (
            <div className="space-y-2">
              <Input value={column.title || ""} onChange={(e) => update({ title: e.target.value })} placeholder="Column title" className={`${inputClass} text-xs h-8`} />
              <DragList
                items={column.links || []}
                onChange={(links) => update({ links })}
                emptyText="No links"
                addLabel="Add link"
                onAdd={() => update({ links: [...(column.links || []), { label: "", href: "" }] })}
                renderItem={(link, _li, helpers) => (
                  <div className="grid grid-cols-2 gap-1">
                    <Input value={link.label || ""} onChange={(e) => helpers.update({ label: e.target.value })} placeholder="Label" className={`${inputClass} text-xs h-7`} />
                    <Input value={link.href || ""} onChange={(e) => helpers.update({ href: e.target.value })} placeholder="/path" className={`${inputClass} text-xs h-7`} />
                  </div>
                )}
              />
            </div>
          )}
        />
      </Field>

      <Field label="Contact rows" hint="Drag to reorder">
        <DragList
          items={data.contact || []}
          onChange={(contact) => setData({ ...data, contact })}
          emptyText="No contact rows"
          addLabel="Add row"
          onAdd={() => setData({ ...data, contact: [...(data.contact || []), { icon: "MAIL", text: "" }] })}
          renderItem={(item, _i, { update }) => (
            <div className="grid grid-cols-[80px_1fr] gap-1.5">
              <Input value={item.icon || ""} onChange={(e) => update({ icon: e.target.value })} placeholder="MAIL" className={`${inputClass} text-xs h-8`} />
              <Input value={item.text || ""} onChange={(e) => update({ text: e.target.value })} placeholder="Detail" className={`${inputClass} text-xs h-8`} />
            </div>
          )}
        />
      </Field>
    </StudioShell>
  );
}

const PANEL_MAP = {
  brand: BrandPanel,
  hero: HeroPanel,
  about: AboutPanel,
  "services-header": ServicesHeaderPanel,
  "section-headers": SectionHeadersPanel,
  cta: CtaPanel,
  careers: CareersPanel,
  contact: ContactPanel,
  footer: FooterPanel,
};

export default function SiteSettings() {
  const [active, setActive] = useState("brand");
  const Active = PANEL_MAP[active] || BrandPanel;

  return (
    <div>
      <PageHeader
        title="Brand & Pages"
        subtitle="Studio editor for brand identity and site-wide page copy. Drag list items to reorder — preview updates live."
      />
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4 items-start">
        <GlassCard className="p-2 lg:sticky lg:top-20">
          <div className="px-2 pt-2 pb-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-white/35">Content studio</div>
          </div>
          <nav className="space-y-0.5">
            {PANELS.map((p) => {
              const I = Icons[p.icon] || Icons.Circle;
              const on = active === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setActive(p.key)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-colors ${
                    on
                      ? "bg-white/[0.1] text-white shadow-[inset_0_0_0_1px_rgba(34,211,238,0.25)]"
                      : "text-white/60 hover:bg-white/[0.04] hover:text-white/90"
                  }`}
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-cyan-400/15 text-cyan-100" : "bg-white/[0.04] text-white/50"}`}>
                    <I className="w-4 h-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{p.label}</span>
                    <span className="block text-[10px] text-white/35 truncate">{p.blurb}</span>
                  </span>
                </button>
              );
            })}
          </nav>
        </GlassCard>
        <div className="min-w-0">
          <Active />
        </div>
      </div>
    </div>
  );
}
